import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Settings, Search, Loader2, Lock, Unlock, Sparkles, RefreshCw, Eye, Download, Zap, CheckCircle2, Circle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LLB_MODULES } from "@/lib/llb-modules";
import {
  listLlbModuleSettings,
  adminUpsertLlbModuleSetting,
} from "@/lib/api/llb-tutoring.functions";
import {
  adminListLlbNotesCache,
  adminRefreshLlbNote,
  adminPrewarmLlbNotes,
  adminBulkRegenerateLlbNotes,
  adminRetryFailedLlbNotes,
} from "@/lib/api/llb-notes-cache.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/llb-modules")({
  component: AdminLlbModules,
});

type Row = {
  module_code: string;
  title: string;
  level: number;
  access: "free" | "enrolled";
  notes_enabled: boolean;
  allow_free_tailored: boolean;
};

function AdminLlbModules() {
  const load = useServerFn(listLlbModuleSettings);
  const save = useServerFn(adminUpsertLlbModuleSetting);
  const loadCache = useServerFn(adminListLlbNotesCache);
  const refreshCache = useServerFn(adminRefreshLlbNote);
  const prewarmCache = useServerFn(adminPrewarmLlbNotes);
  const bulkRegen = useServerFn(adminBulkRegenerateLlbNotes);
  const retryFailed = useServerFn(adminRetryFailedLlbNotes);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin-llb-module-settings"],
    queryFn: () => load(),
  });

  const cacheQ = useQuery({
    queryKey: ["admin-llb-notes-cache"],
    queryFn: () => loadCache(),
    refetchInterval: 30_000,
  });

  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [prewarming, setPrewarming] = useState(false);
  const [bulk, setBulk] = useState<null | {
    scope: "all" | "uncached";
    total: number;
    done: number;
    current: string | null;
    failed: { code: string; error?: string }[];
    cancel: boolean;
    running: boolean;
  }>(null);

  const cacheByCode = useMemo(() => {
    const m = new Map<string, { cached: boolean; bytes: number; cachedAt: string | null }>();
    for (const r of (cacheQ.data?.rows ?? []) as any[]) {
      m.set(r.code, { cached: r.cached, bytes: r.bytes, cachedAt: r.cachedAt });
    }
    return m;
  }, [cacheQ.data]);

  async function handleRefresh(code: string) {
    setRefreshing(code);
    try {
      await refreshCache({ data: { moduleCode: code } });
      toast.success(`${code} notes regenerated`);
      qc.invalidateQueries({ queryKey: ["admin-llb-notes-cache"] });
    } catch (e: any) {
      toast.error("Refresh failed", { description: e?.message ?? String(e) });
    } finally {
      setRefreshing(null);
    }
  }

  async function handlePrewarm() {
    setPrewarming(true);
    try {
      const out = await prewarmCache({ data: { limit: 3 } });
      toast.success(`Prewarmed ${out.processed} module(s)`);
      qc.invalidateQueries({ queryKey: ["admin-llb-notes-cache"] });
    } catch (e: any) {
      toast.error("Prewarm failed", { description: e?.message ?? String(e) });
    } finally {
      setPrewarming(false);
    }
  }

  async function handleBulk(scope: "all" | "uncached") {
    if (bulk?.running) return;
    const initial = { scope, total: 0, done: 0, current: null, failed: [], cancel: false, running: true } as NonNullable<typeof bulk>;
    setBulk(initial);
    let state = { ...initial };
    let index = 0;
    try {
      // Loop until done. Each batch processes 2 modules at a time.
      // We mutate `state` then sync via setBulk so React renders progress.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (state.cancel) break;
        const out: any = await bulkRegen({ data: { scope, batchSize: 2, startIndex: index } });
        state.total = out.totalTargets;
        state.done += out.processedInBatch;
        index = out.nextIndex;
        const newFailed = (out.results as any[]).filter((r) => !r.ok).map((r) => ({ code: r.code, error: r.error }));
        state.failed = [...state.failed, ...newFailed];
        state.current = (out.results as any[]).at(-1)?.code ?? null;
        setBulk({ ...state });
        qc.invalidateQueries({ queryKey: ["admin-llb-notes-cache"] });
        if (out.done) break;
        // Read latest cancel flag from React state
        setBulk((cur) => { state.cancel = !!cur?.cancel; return cur; });
        if (state.cancel) break;
      }
      toast.success(
        state.cancel
          ? `Bulk regenerate cancelled at ${state.done}/${state.total}`
          : `Bulk regenerate complete: ${state.done}/${state.total} (${state.failed.length} failed)`,
      );
    } catch (e: any) {
      toast.error("Bulk regenerate failed", { description: e?.message ?? String(e) });
    } finally {
      state.running = false;
      setBulk({ ...state });
    }
  }

  function cancelBulk() {
    setBulk((b) => (b ? { ...b, cancel: true } : b));
  }

  const [retry, setRetry] = useState<null | {
    total: number;
    done: number;
    current: string | null;
    failed: { code: string; error?: string }[];
    cancel: boolean;
    running: boolean;
  }>(null);

  async function handleRetryFailed() {
    const codes = (bulk?.failed ?? []).map((f) => f.code);
    if (!codes.length) {
      toast.info("No failed modules to retry");
      return;
    }
    if (retry?.running) return;
    const initial = { total: codes.length, done: 0, current: null, failed: [], cancel: false, running: true } as NonNullable<typeof retry>;
    setRetry(initial);
    const state = { ...initial };
    let index = 0;
    try {
      while (true) {
        if (state.cancel) break;
        const out: any = await retryFailed({ data: { codes, batchSize: 2, startIndex: index, deep: true } });
        state.done += out.processedInBatch;
        index = out.nextIndex;
        const newFailed = (out.results as any[]).filter((r) => !r.ok).map((r) => ({ code: r.code, error: r.error }));
        state.failed = [...state.failed, ...newFailed];
        state.current = (out.results as any[]).at(-1)?.code ?? null;
        setRetry({ ...state });
        qc.invalidateQueries({ queryKey: ["admin-llb-notes-cache"] });
        if (out.done) break;
        setRetry((cur) => { state.cancel = !!cur?.cancel; return cur; });
        if (state.cancel) break;
      }
      // Drop the recovered codes from the original bulk-failed list so
      // a second "Retry failed" only re-tries what is still failing.
      const stillFailed = new Set(state.failed.map((f) => f.code));
      setBulk((b) => (b ? { ...b, failed: b.failed.filter((f) => stillFailed.has(f.code)) } : b));
      toast.success(
        state.cancel
          ? `Retry cancelled at ${state.done}/${state.total}`
          : `Retry complete: ${state.done - state.failed.length} recovered, ${state.failed.length} still failing`,
      );
    } catch (e: any) {
      toast.error("Retry failed", { description: e?.message ?? String(e) });
    } finally {
      state.running = false;
      setRetry({ ...state });
    }
  }



  const rows: Row[] = useMemo(() => {
    const byCode = new Map(
      ((q.data?.settings as any[]) ?? []).map((s) => [s.module_code, s]),
    );
    return LLB_MODULES.map((m) => {
      const s = byCode.get(m.code) ?? {};
      return {
        module_code: m.code,
        title: m.title,
        level: m.level,
        access: (s.access ?? "enrolled") as "free" | "enrolled",
        notes_enabled: s.notes_enabled ?? true,
        allow_free_tailored: s.allow_free_tailored ?? false,
      };
    });
  }, [q.data]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        r.module_code.toLowerCase().includes(s) ||
        r.title.toLowerCase().includes(s),
    );
  }, [rows, search]);

  async function persist(r: Row, patch: Partial<Row>) {
    const next = { ...r, ...patch };
    setPending(r.module_code);
    try {
      await save({
        data: {
          moduleCode: next.module_code,
          access: next.access,
          notesEnabled: next.notes_enabled,
          allowFreeTailored: next.allow_free_tailored,
        },
      });
      toast.success(`${r.module_code} saved`);
      qc.invalidateQueries({ queryKey: ["admin-llb-module-settings"] });
    } catch (e: any) {
      toast.error("Save failed", { description: e?.message ?? String(e) });
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy flex items-center gap-2">
            <Settings className="h-5 w-5" /> LLB module configuration
          </h1>
          <p className="text-sm text-muted-foreground">
            Choose which modules are free-preview vs reserved for enrolled students,
            and control whether tailored AI notes can be generated for each module.
          </p>
        </div>
      </header>

      <section className="rounded-xl border border-gold/40 bg-gold/5 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-navy flex items-center gap-1">
              <Zap className="h-3 w-3 text-gold" /> 8-page tutor notes cache
            </p>
            <p className="mt-1 text-sm">
              <span className="font-semibold text-navy">{cacheQ.data?.cachedCount ?? 0}</span>
              <span className="text-foreground/70"> of {cacheQ.data?.total ?? LLB_MODULES.length} modules prewarmed.</span>
            </p>
            <p className="text-xs text-foreground/60">
              A background job prewarms 2 modules every 10 minutes. Use Refresh on a row after a UNISA curriculum or statute change.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={handlePrewarm} disabled={prewarming || !!bulk?.running}>
              {prewarming ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
              Prewarm 3
            </Button>
            <Button size="sm" onClick={() => handleBulk("uncached")} disabled={!!bulk?.running}>
              <Zap className="h-3 w-3 mr-1" /> Bulk: all uncached
            </Button>
            <Button size="sm" variant="destructive" onClick={() => handleBulk("all")} disabled={!!bulk?.running}>
              <RefreshCw className="h-3 w-3 mr-1" /> Force regenerate ALL
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleRetryFailed}
              disabled={!!bulk?.running || !!retry?.running || !(bulk?.failed?.length)}
              title="Regenerate only the modules that failed in the most recent bulk run"
            >
              {retry?.running ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Retry failed{bulk?.failed?.length ? ` (${bulk.failed.length})` : ""}
            </Button>
          </div>
        </div>

        {retry && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50/60 p-3">
            <p className="text-xs font-semibold text-amber-900">
              {retry.running ? "Retrying failed modules" : retry.cancel ? "Retry cancelled" : "Retry completed"}
            </p>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: retry.total ? `${Math.min(100, (retry.done / retry.total) * 100)}%` : "0%" }}
              />
            </div>
            <p className="mt-2 text-[11px] text-amber-900/80">
              {retry.done}/{retry.total} processed
              {retry.current ? ` · last: ${retry.current}` : ""}
              {retry.failed.length ? ` · ${retry.failed.length} still failing` : ""}
            </p>
            {retry.running && (
              <Button size="sm" variant="ghost" className="mt-1" onClick={() => setRetry((b) => (b ? { ...b, cancel: true } : b))} disabled={retry.cancel}>
                {retry.cancel ? "Stopping…" : "Cancel"}
              </Button>
            )}
          </div>
        )}


        {bulk && (
          <div className="mt-4 rounded-lg border border-navy/20 bg-white p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-navy">
                {bulk.running ? "Regenerating" : bulk.cancel ? "Cancelled" : "Completed"} · scope: {bulk.scope}
              </p>
              {bulk.running && (
                <Button size="sm" variant="ghost" onClick={cancelBulk} disabled={bulk.cancel}>
                  {bulk.cancel ? "Stopping…" : "Cancel"}
                </Button>
              )}
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-gold transition-all"
                style={{ width: bulk.total ? `${Math.min(100, (bulk.done / bulk.total) * 100)}%` : "0%" }}
              />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {bulk.done}/{bulk.total || "?"} processed
              {bulk.current ? ` · last: ${bulk.current}` : ""}
              {bulk.failed.length ? ` · ${bulk.failed.length} failed` : ""}
            </p>
            {bulk.failed.length > 0 && (
              <details className="mt-2 text-[11px]">
                <summary className="cursor-pointer text-red-700">Show {bulk.failed.length} failure(s)</summary>
                <ul className="mt-1 list-disc pl-5 text-red-700">
                  {bulk.failed.slice(-20).map((f) => (
                    <li key={f.code}><span className="font-mono">{f.code}</span> — {f.error ?? "error"}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </section>

      <div className="rounded-xl border border-border bg-card p-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by module code or title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border-0 shadow-none focus-visible:ring-0"
        />
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Module</th>
                <th className="px-3 py-2 text-left">Level</th>
                <th className="px-3 py-2 text-left">Access</th>
                <th className="px-3 py-2 text-left">Tailored notes</th>
                <th className="px-3 py-2 text-left">Free users may generate</th>
                <th className="px-3 py-2 text-left">8-page notes</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.module_code} className="border-t border-border align-middle">
                  <td className="px-3 py-2">
                    <p className="font-mono text-xs font-semibold text-navy">{r.module_code}</p>
                    <p className="text-xs text-muted-foreground">{r.title}</p>
                  </td>
                  <td className="px-3 py-2 text-xs">L{r.level}</td>
                  <td className="px-3 py-2">
                    <select
                      value={r.access}
                      onChange={(e) =>
                        persist(r, { access: e.target.value as "free" | "enrolled" })
                      }
                      className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                    >
                      <option value="enrolled">Enrolled only</option>
                      <option value="free">Free preview</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={r.notes_enabled}
                        onChange={(e) => persist(r, { notes_enabled: e.target.checked })}
                      />
                      <Sparkles className="h-3 w-3 text-gold" /> Enabled
                    </label>
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={r.allow_free_tailored}
                        disabled={r.access !== "free"}
                        onChange={(e) =>
                          persist(r, { allow_free_tailored: e.target.checked })
                        }
                      />
                      {r.access === "free" ? (
                        <Unlock className="h-3 w-3 text-emerald-600" />
                      ) : (
                        <Lock className="h-3 w-3 text-muted-foreground" />
                      )}
                      {r.access === "free" ? "Allowed" : "Locked"}
                    </label>
                  </td>
                  <td className="px-3 py-2">
                    {(() => {
                      const c = cacheByCode.get(r.module_code);
                      const cached = !!c?.cached;
                      return (
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                          {cached ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                              <CheckCircle2 className="h-3 w-3" /> Cached
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                              <Circle className="h-3 w-3" /> Not cached
                            </span>
                          )}
                          <a
                            href={`/api/public/llb-module-notes/${encodeURIComponent(r.module_code)}?format=html`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-input px-1.5 py-0.5 hover:bg-muted"
                            title="Preview in browser"
                          >
                            <Eye className="h-3 w-3" /> Preview
                          </a>
                          <a
                            href={`/api/public/llb-module-notes/${encodeURIComponent(r.module_code)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-input px-1.5 py-0.5 hover:bg-muted"
                            title="Download PDF"
                          >
                            <Download className="h-3 w-3" /> PDF
                          </a>
                          <button
                            onClick={() => handleRefresh(r.module_code)}
                            disabled={refreshing === r.module_code}
                            className="inline-flex items-center gap-1 rounded-md border border-navy/30 bg-navy/5 px-1.5 py-0.5 text-navy hover:bg-navy/10 disabled:opacity-50"
                            title="Regenerate notes (use after a curriculum or statute change)"
                          >
                            {refreshing === r.module_code ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3" />
                            )}
                            Refresh
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {pending === r.module_code ? (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
